import { Trans, useTranslation } from "@outschool/localization";
import { gql } from "@outschool/ui-apollo";
import { Checkbox, Label } from "@outschool/ui-legacy-component-library";
import React, { useState } from "react";

import {
  learnerOutsideStrictAgeRange,
  toddlerIsNotAllowed,
} from "../../../../../shared/Activity";
import { LearnerItem_ActivityFragment } from "../../../../../shared/generated/graphql";
import {
  EnrollmentWarnings,
  OneOnOneEnrolledInOtherSectionWarning,
  StrictAgeRangeWarning,
  ToddlerNotAllowedWarning,
} from "./EnrollmentWarnings";
import { LearnerItemType } from "./utils";

type LearnerItemProps = {
  disabled: boolean;
  onUpdate?(learnerItem: LearnerItemType): void;
  learnerItem: LearnerItemType;
  activity: LearnerItem_ActivityFragment;
  ignoreStrictAgeRange: boolean;
  requestAgeRangeException?: (learnerItemType: LearnerItemType) => void;
};

export default function LearnerItem({
  onUpdate = () => {},
  disabled,
  learnerItem,
  activity,
  requestAgeRangeException,
  ignoreStrictAgeRange,
}: LearnerItemProps) {
  const { t } = useTranslation(
    "client\\components\\enrolling\\enrollmentForm\\learnerItems\\LearnerItem",
  );
  const {
    learner,
    checked,
    alreadyEnrolled,
    prepay,
    alreadyWaitlisted,
    conflictingEnrollment,
    pendingEnrollment,
    hasOverlappingPendingEnrollmentMeetingPurchase,
    otherSectionEnrollment,
  } = learnerItem;

  const outsideStrictAgeRange = learnerOutsideStrictAgeRange(
    activity,
    ignoreStrictAgeRange,
    learnerItem.learner.age ?? 0,
  );

  const shouldBlockToddler = toddlerIsNotAllowed(
    activity,
    learnerItem.learner.age ?? 0,
  );

  const isOneOnOne =
    !!activity.hasTeacherSchedule &&
    activity.size_max === 1 &&
    activity.size_min === 1;
  const [stateDisabled, setDisabled] = useState(false);
  const [showStrictAgeWarning, setShowStrictAgeWarning] = useState(false);
  const [showToddlerWarning, setShowToddlerWarning] = useState(false);
  const finalDisabled =
    stateDisabled ||
    (alreadyEnrolled && !prepay) ||
    learnerItem.disabled ||
    disabled;
  const checkboxId = `${learnerItem.key}-checkbox`;
  const labelSx = {
    paddingY: "0.1em",
    paddingX: "small",
    marginLeft: "small",
    fontWeight: 500,
  };
  return (
    <div>
      <div>
        <label style={finalDisabled ? { color: "grey" } : {}}>
          <Checkbox
            id={checkboxId}
            data-test-id="learner-item-checkbox"
            sx={{ marginRight: "0.5em", marginBottom: 0 }}
            checked={checked}
            disabled={finalDisabled}
            onChange={e => {
              // We don't let the user check the box if they are outside the strict age range
              if (outsideStrictAgeRange && e.currentTarget.checked) {
                e.preventDefault();
                setDisabled(true);
                setShowStrictAgeWarning(true);
                // We don't let toddlers enroll in non-toddler classes regardless of age range strictness
              } else if (shouldBlockToddler && e.currentTarget.checked) {
                e.preventDefault();
                setDisabled(true);
                setShowToddlerWarning(true);
              } else {
                onUpdate({
                  ...learnerItem,
                  checked: e.currentTarget.checked,
                });
              }
            }}
            label={
              <>
                <span className="amp-block" data-private>
                  {learner.name}
                </span>{" "}
                {/* @ts-ignore TS(2531): Object is possibly 'null'. */}
                {learner.age > 0 ? (
                  <>
                    <Trans t={t}>
                      (age{" "}
                      <span className="amp-block" data-private>
                        {{ learnerAge: learner.age }}
                      </span>
                      )
                    </Trans>
                  </>
                ) : null}
                &nbsp;
                {alreadyEnrolled && (
                  <Label
                    color="gray500"
                    sx={labelSx}
                  >{t`already enrolled`}</Label>
                )}
                {pendingEnrollment && (
                  <Label color="gray700" sx={labelSx}>
                    {!isOneOnOne ||
                    !hasOverlappingPendingEnrollmentMeetingPurchase
                      ? t`pending enrollment`
                      : t`overlapping pending enrollment`}
                  </Label>
                )}
                {alreadyWaitlisted && (
                  <Label color="gray700" sx={labelSx}>{t`waitlisted`}</Label>
                )}
                {isOneOnOne && (
                  <OneOnOneEnrolledInOtherSectionWarning
                    sx={{ marginLeft: "0.5em" }}
                    otherSectionEnrollment={otherSectionEnrollment}
                  />
                )}
              </>
            }
          />
        </label>
        {!alreadyEnrolled && checked && (
          <EnrollmentWarnings
            learner={learner}
            activity={activity}
            ignoreStrictAgeRange={ignoreStrictAgeRange}
            conflictingEnrollment={conflictingEnrollment}
            otherSectionEnrollment={otherSectionEnrollment}
          />
        )}
        {showStrictAgeWarning && (
          <StrictAgeRangeWarning
            activity={activity}
            ignoreStrictAgeRange={ignoreStrictAgeRange}
            learner={learner}
            requestException={requestAgeRangeException!}
          />
        )}
        {showToddlerWarning && (
          <ToddlerNotAllowedWarning activity={activity} learner={learner} />
        )}
      </div>
    </div>
  );
}

LearnerItem.fragments = {
  activity: gql`
    fragment LearnerItem_activity on Activity {
      uid
      strictAgeRange {
        min
        max
      }
      hasTeacherSchedule
      size_max
      size_min
      ...EnrollmentWarnings_activity
    }
    ${EnrollmentWarnings.fragments.activity}
  `,
  section: gql`
    fragment LearnerItem_section on Section {
      ...EnrollmentWarnings_section
    }
    ${EnrollmentWarnings.fragments.section}
  `,
  learner: gql`
    fragment LearnerItem_learner on Learner {
      uid
      name
      age
      ...EnrollmentWarnings_learner
    }
    ${EnrollmentWarnings.fragments.learner}
  `,
};
